/**
 * analytics.js — Centralized tracking for Stronger Bot
 * GA4 + Microsoft Clarity custom events
 *
 * Usage:
 *   import { track } from "@/lib/analytics";
 *   track("exit_intent_shown");
 *   track("signup_click", { source: "exit_intent" });
 */

const isDev = process.env.NODE_ENV === "development";

/**
 * Send a custom event to GA4 + Clarity
 * @param {string} eventName
 * @param {object} params
 */
export const track = (eventName, params = {}) => {
  if (isDev) {
    console.log(`[Analytics] ${eventName}`, params);
    return;
  }

  // Google Analytics 4
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, {
      ...params,
      timestamp: new Date().toISOString(),
    });
  }

  // Microsoft Clarity custom tag
  if (typeof window !== "undefined" && window.clarity) {
    window.clarity("set", eventName, JSON.stringify(params));
  }
};

/**
 * Track page view (called automatically by React Router on each
 * location change).
 *
 * Why this is a `gtag("event", "page_view", ...)` call rather than
 * `gtag("config", ...)`:
 *
 * - `gtag("config", ...)` is for INITIAL Measurement-ID setup, not
 *   recurring page views. Calling it on every route change re-runs the
 *   library bootstrap and only updates `page_path` — it does NOT pick
 *   up the route-specific document.title, so GA dashboards previously
 *   grouped every pageview under the static index.html title
 *   "Stronger Bot - AI Assistant" regardless of route.
 * - The correct pattern is `gtag("event", "page_view", {…})` with the
 *   title + path explicitly. Combined with `send_page_view: false` on
 *   the initial config in index.html, this gives us exactly one
 *   page_view per route, with the right title each time.
 *
 * queueMicrotask defers the read of document.title by one tick so
 * react-helmet-async has finished applying the new <title> before we
 * grab it. Without this, the FIRST page_view after a hard navigation
 * would still see the previous title.
 */
export const trackPageView = (path) => {
  if (isDev) {
    console.log(`[Analytics] pageview: ${path}`);
    return;
  }
  if (typeof window === "undefined" || typeof window.gtag !== "function")
    return;
  const fire = () => {
    try {
      window.gtag("event", "page_view", {
        page_path: path,
        page_title: document.title,
        page_location: window.location.href,
      });
    } catch {
      // Analytics must never break the app.
    }
  };
  if (typeof queueMicrotask === "function") {
    queueMicrotask(fire);
  } else {
    setTimeout(fire, 0);
  }
};

// ── Events prédéfinis ──────────────────────────────────────

/** Visiteur voit le popup exit intent */
export const trackExitIntentShown = () => track("exit_intent_shown");

/** Visiteur clique sur le CTA du exit intent */
export const trackExitIntentClick = () =>
  track("exit_intent_cta_click", { source: "exit_intent_popup" });

/** Visiteur clique sur la sticky CTA bar */
export const trackStickyCTAClick = () =>
  track("sticky_cta_click", { source: "sticky_bar" });

/** Upsell modal affiché (limite atteinte) */
export const trackUpsellShown = (reason) =>
  track("upsell_modal_shown", { reason });

/** Utilisateur clique sur upgrade dans l'upsell modal */
export const trackUpsellClick = (reason) =>
  track("upsell_cta_click", { reason, source: "upsell_modal" });

/** Inscription réussie */
export const trackSignup = (source = "direct") => track("sign_up", { source });

/** Clic sur un plan de pricing */
export const trackPlanClick = (planId, billingPeriod) =>
  track("plan_selected", { plan_id: planId, billing_period: billingPeriod });

/** Checkout initié — GA4 `begin_checkout` (signal de conversion payante, ADS_ASSETS.md §5) */
export const trackCheckoutStart = (planId, billingPeriod) =>
  track("begin_checkout", {
    plan_id: planId,
    billing_period: billingPeriod,
    currency: "EUR",
  });

/** Page use case visitée */
export const trackUseCaseView = (slug) => track("use_case_view", { slug });

/** Article de blog lu */
export const trackBlogView = (slug) => track("blog_post_view", { slug });

/** Message envoyé dans le chat */
export const trackMessageSent = (model) =>
  track("chat_message_sent", { model });

/** Document uploadé */
export const trackDocumentUpload = (fileType) =>
  track("document_uploaded", { file_type: fileType });

/** Image générée */
export const trackImageGenerated = () => track("image_generated");
